import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import {
  addNavigate,
  addOptionalQueryParam,
} from 'shared/src/utils/addNavigate'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Button } from '../Button'
import { IconButton } from '../IconButton'
import { SIDE_PADDING } from '../PageConstants'
import { TextFieldNew } from '../TextField'
import { SANS_SERIF_FONT_FAMILY, SERIF_FONT_FAMILY } from '../styles'
import { EmailVerification } from './EmailVerification'
import { SetPassword } from './SetPassword'

const useStyles = createUseStyles({
  container: {
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    height: '100vh',
  },
  title: {
    fontFamily: SERIF_FONT_FAMILY,
    fontSize: 32,
    fontWeight: 700,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(144, 115, 79, 0.8)',
    marginLeft: 30,
    marginBottom: 40,
  },
  italics: {
    fontStyle: 'italic',
    fontWeight: 600,
  },
  alreadyRegisteredText: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 14,
    color: 'rgba(54, 52, 50, 0.8)',
    lineHeight: '187%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 200,
    width: '100%',
    gap: 2,
  },
  linkText: {
    fontWeight: 900,
    color: '#CBA67A',
    '&:hover': {
      cursor: 'pointer',
    },
  },
})

type VerificationState = 'unknown' | 'pending' | 'verified'

export const Register = flowMax(
  addDisplayName('Register'),
  addOptionalQueryParam('user'),
  addOptionalQueryParam('code'),
  addNavigate,
  addState('email', 'onChangeEmail', ({ user }) => user ?? ''),
  addState('password', 'onChangePassword', ''),
  addState(
    'verificationState',
    'setVerificationState',
    ({ user, code }): VerificationState =>
      user && code ? 'verified' : 'unknown'
  ),
  addApi,
  addTranslationHelpers,
  addHandlers({
    onBack:
      ({ navigate }) =>
      () => {
        navigate('/')
      },
    onContinue:
      ({ api, setVerificationState, email }) =>
      () => {
        const getVerificationStatus = async () => {
          await api.auth.status(email, 'creatorWeb').then((response) => {
            if (response.status === 'notFound') {
              api.auth.createVerificationEmail({ email, source: 'creatorWeb' })
            }
            setVerificationState('pending')
          })
        }

        getVerificationStatus()
      },
    onClose:
      ({ setVerificationState }) =>
      () => {
        setVerificationState('unknown')
      },
    onLogin:
      ({ navigate }) =>
      () => {
        navigate('/login')
      },
  }),
  addHandlers({
    onKeyPressContinue:
      ({ onContinue, email }) =>
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && email !== '') {
          onContinue()
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    email,
    onChangeEmail,
    onBack,
    onContinue,
    onClose,
    onLogin,
    verificationState,
    onKeyPressContinue,
    user,
    code,
    classes,
    t,
  }) => (
    <div className={classes.container}>
      {verificationState === 'pending' ? (
        <EmailVerification
          emailType="verification"
          email={email}
          onClose={onClose}
        />
      ) : (
        <>
          {verificationState === 'verified' && user && code ? (
            <SetPassword email={user} code={code} />
          ) : (
            <div>
              <IconButton name="backRodeo" onClick={onBack} />
              <h1 className={classes.title}>{t('auth.register.title')}</h1>
              <p className={classes.subtitle}>{t('auth.register.subtitle')}</p>
              <TextFieldNew
                id="email"
                label={t('auth.fields.email')}
                value={email ?? ''}
                onChange={onChangeEmail}
                focusOnMount
                showClearInput={false}
                onKeyPress={onKeyPressContinue}
              />
              <Button
                text={t('auth.continue')}
                type="fullWidth"
                onClick={onContinue}
                disabled={email === ''}
              />
            </div>
          )}
          <div className={classes.alreadyRegisteredText}>
            {t('auth.register.alreadyRegistered')}
            <span className={classes.linkText} onClick={onLogin}>
              {t('auth.register.login')}
            </span>
          </div>
        </>
      )}
    </div>
  )
)
